import {
    ACESFilmicToneMapping,
    Box3,
    Clock,
    Group,
    VSMShadowMap,
    PerspectiveCamera,
    Scene,
    SRGBColorSpace,
    TextureLoader,
    Vector2,
    Vector3,
    WebGLRenderer,
} from 'three'
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import Stats from 'stats.js'
import gsap from 'gsap'

import { createGLTFLoader } from './Loaders.js'
import { loadEnvironmentMap } from './EnvironmentMap.js'
import { Lights } from './Lights.js'
import { Ground } from './Ground.js'
import { PhoneModel } from './PhoneModel.js'
import { HtmlPlane } from './HtmlPlane.js'

export class App {
    constructor({
        canvas,
        container,
        assetsBase = '',
        htmlScreen = null,
        useBloom = false,
        enableDebug = false,
        enableStats = false,
        enableShadows = true,
        imageTexture = null,
        maskTexture = null,
        orbitControls = false,
        autoMove = true,
    } = {}) {
        this.canvas = canvas
        this.container = container
        this.assetsBase = assetsBase
        this.htmlScreen = imageTexture ? null : htmlScreen
        this.currentSection = 0
        this.enableShadows = enableShadows
        this.orbitEnabled = !!orbitControls
        this.autoMove = !!autoMove && !this.orbitEnabled
        this.clock = new Clock()

        this.sizes = {
            width: container?.clientWidth || window.innerWidth,
            height: container?.clientHeight || window.innerHeight,
        }

        this._initScene()
        this._initCamera()
        this._initRenderer()
        this._initPostProcessing(useBloom)

        this.lights = new Lights(this.scene)
        this.ground = new Ground(this.scene)

        this.envMap = loadEnvironmentMap(this.assetsBase)
        this.scene.environment = this.envMap
        this.scene.environmentIntensity = 1.6
        this.gltfLoader = createGLTFLoader(
            this.assetsBase ? `${this.assetsBase}/draco/` : undefined,
        )

        this.phone = new PhoneModel({
            loader: this.gltfLoader,
            assetsBase: this.assetsBase,
            envMap: this.envMap,
            parent: this.gameHolder,
        })

        let planeTexture = null
        if (imageTexture) {
            if (typeof imageTexture === 'string') {
                planeTexture = new TextureLoader().load(imageTexture)
                planeTexture.colorSpace = SRGBColorSpace
            } else {
                planeTexture = imageTexture
            }
        }

        let maskTex = null
        if (maskTexture) {
            maskTex = typeof maskTexture === 'string'
                ? new TextureLoader().load(maskTexture)
                : maskTexture
        }

        this.htmlPlane = new HtmlPlane({ parent: this.gameHolder, imageTexture: planeTexture, maskTexture: maskTex })
        this.htmlPlane.mesh.visible = false

        if (this.htmlScreen) {
            this.htmlScreen.connect(this.camera, this.renderer)
            this.htmlScreen.attach(this.htmlPlane.mesh)
        }

        if (this.orbitEnabled) this._initOrbitControls()

        // Reparent onto the phone's screen mesh once loaded, then reveal.
        this.phone.screenMeshReady.then((screenMesh) => {
            screenMesh.add(this.htmlPlane.mesh)
            this.htmlPlane.mesh.position.set(0, 0, -0.02)
            this.htmlPlane.mesh.rotation.set(0, Math.PI, 0)
            this.htmlPlane.mesh.scale.set(0.4, 0.5, 0.5)
            this.htmlPlane.mesh.visible = true
        })

        this.phone.meshReady.then((phoneMesh) => this._fitCameraToMesh(phoneMesh))

        if (enableStats) this._initStats()
        this._bindEvents()
        this._resize()
        this.renderer.setAnimationLoop(() => this._tick())
    }

    _initScene() {
        this.scene = new Scene()

        this.user = new Group()
        this.scene.add(this.user)

        this.gameHolder = new Group()
        this.gameHolder.rotation.x = -0.2
        this.scene.add(this.gameHolder)
    }

    _initCamera() {
        this.camera = new PerspectiveCamera(
            45,
            this.sizes.width / this.sizes.height,
            0.1,
            100,
        )
        this.camera.position.set(0, 0.2, 4)
        this.camera.lookAt(0, 0, 0)
        this.user.add(this.camera)
    }

    _initRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        })
        //this.renderer.outputColorSpace = SRGBColorSpace
        this.renderer.toneMapping = ACESFilmicToneMapping
        this.renderer.toneMappingExposure = 0.81
        this.renderer.shadowMap.enabled = this.enableShadows
        this.renderer.shadowMap.type = VSMShadowMap
        this.renderer.setSize(this.sizes.width, this.sizes.height)
        this.renderer.setPixelRatio(1)
    }

    _initPostProcessing(useBloom) {
        if (!useBloom) {
            this.composer = null
            return
        }
        this.composer = new EffectComposer(this.renderer)
        this.composer.addPass(new RenderPass(this.scene, this.camera))
        this.bloomPass = new UnrealBloomPass(
            new Vector2(window.innerWidth, window.innerHeight),
            0.15,
            0.4,
            0.9,
        )
        this.composer.addPass(this.bloomPass)
    }

    _fitCameraToMesh(mesh, margin = 1.25) {
        const box = new Box3().setFromObject(mesh)
        const size = new Vector3()
        box.getSize(size)

        const fovRad = (this.camera.fov * Math.PI) / 180
        const aspect = this.camera.aspect
        const distForHeight = (size.y / 2) / Math.tan(fovRad / 2)
        const hFov = 2 * Math.atan(Math.tan(fovRad / 2) * aspect)
        const distForWidth = (size.x / 2) / Math.tan(hFov / 2)

        this.camera.position.z = Math.max(distForHeight, distForWidth) * margin
        this.camera.updateProjectionMatrix()
    }

    _initStats() {
        this.stats = new Stats()
        this.stats.showPanel(0)
        this.stats.dom.style.position = 'fixed'
        this.stats.dom.style.top = '0'
        this.stats.dom.style.left = '0'
        this.stats.dom.style.zIndex = '10000'
        document.body.appendChild(this.stats.dom)
    }

    _bindEvents() {
        window.addEventListener('resize', () => this._resize())
        if (this.autoMove) {
            window.addEventListener('mousemove', (e) => this._onMouseMove(e))
        }
        document.addEventListener('DOMContentLoaded', () => this._resize())
    }

    _initOrbitControls() {
        this.controls = new OrbitControls(this.camera, this.canvas)
        this.controls.enableDamping = true
        this.controls.dampingFactor = 0.08
        this.controls.target.set(0, 0.2, 0)
        this.controls.minDistance = 1
        this.controls.maxDistance = 15
        this.controls.update()
    }

    _resize() {
        const width = this.container?.clientWidth || window.innerWidth
        const height = this.container?.clientHeight || window.innerHeight

        this.sizes.width = width
        this.sizes.height = height

        this.camera.aspect = width / height
        this.camera.updateProjectionMatrix()

        this.renderer.setSize(width, height)
        this.renderer.setPixelRatio(1)
        this.composer?.setSize(width, height)

        if (this.phone?.mesh && !this.orbitEnabled) this._fitCameraToMesh(this.phone.mesh)
    }

    _onMouseMove(event) {
        const x = event.clientX / this.sizes.width - 0.5
        const y = event.clientY / this.sizes.height - 0.5
        gsap.to(this.gameHolder.rotation, {
            duration: 1.4,
            y: x / 2,
            x: -0.2 + y / 2,
        })
    }

    setShadowsEnabled(enabled) {
        this.enableShadows = !!enabled
        this.renderer.shadowMap.enabled = this.enableShadows
        this.renderer.shadowMap.needsUpdate = true
        this.scene.traverse((obj) => {
            if (obj.isMesh && obj.material) {
                obj.material.needsUpdate = true
            }
        })
    }

    onNewSection(section) {
        if (section === this.currentSection) return
        this.currentSection = section
        this.phone.rotate()
    }

    _tick() {
        this.stats?.begin()
        if (!this.orbitEnabled) this.phone.update()
        this.controls?.update()
        this.htmlScreen?.update()
        if (this.composer) this.composer.render()
        else this.renderer.render(this.scene, this.camera)
        this.stats?.end()
    }

    setMaskTexture(source) {
        const apply = (tex) => this.htmlPlane?.setMask(tex)
        if (!source) return apply(null)
        if (typeof source === 'string') {
            new TextureLoader().load(source, apply)
        } else if (source.isTexture) {
            apply(source)
        } else if (source instanceof File || source instanceof Blob) {
            const url = URL.createObjectURL(source)
            new TextureLoader().load(url, (tex) => {
                apply(tex)
                URL.revokeObjectURL(url)
            })
        }
    }

    setImageTexture(source) {
        const apply = (tex) => {
            tex.colorSpace = SRGBColorSpace
            this.htmlPlane?.setTexture(tex)
        }
        if (typeof source === 'string') {
            new TextureLoader().load(source, apply)
        } else if (source && source.isTexture) {
            apply(source)
        } else if (source instanceof File || source instanceof Blob) {
            const url = URL.createObjectURL(source)
            new TextureLoader().load(url, (tex) => {
                apply(tex)
                URL.revokeObjectURL(url)
            })
        }
    }

    exportImage(filename = 'phone.png') {
        const groundVisible = this.ground?.mesh?.visible
        if (this.ground?.mesh) this.ground.mesh.visible = false

        const prevClearAlpha = this.renderer.getClearAlpha()
        this.renderer.setClearAlpha(0)
        this.renderer.render(this.scene, this.camera)

        const dataURL = this.canvas.toDataURL('image/png')

        this.renderer.setClearAlpha(prevClearAlpha)
        if (this.ground?.mesh) this.ground.mesh.visible = groundVisible

        const a = document.createElement('a')
        a.href = dataURL
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        return dataURL
    }
}
